import React, { useCallback, useEffect, useState } from "react";
import { translateUi } from "./i18n/index";
import { CheckCircle2, ShieldCheck, Trash2 } from "./icons";

type Props = {
  userEmail: string;
};

type TrustedDevice = {
  id: string;
  deviceLabel: string;
  country: string;
  lastUsedAt: string;
  lastMfaAt: string;
  expiresAt: string;
  createdAt: string;
  current?: boolean;
};

function formatWhen(iso: string): string {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return iso || "—";
  try {
    return new Date(ms).toLocaleString();
  } catch {
    return iso;
  }
}

export const TwoFactorSection: React.FC<Props> = ({ userEmail }) => {
  const [enabled, setEnabled] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentCode, setCurrentCode] = useState("");
  const [confirmCode, setConfirmCode] = useState("");
  const [qr, setQr] = useState("");
  const [secret, setSecret] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [devices, setDevices] = useState<TrustedDevice[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/users/me/mfa", { headers: { "X-User-Email": userEmail }, credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setEnabled(!!data.enabled);
        setRemaining(Number(data.backupCodesRemaining) || 0);
      }
    } finally {
      setLoading(false);
    }
  }, [userEmail]);

  const loadDevices = useCallback(async () => {
    setDevicesLoading(true);
    try {
      const res = await fetch("/api/users/me/trusted-devices", {
        headers: { "X-User-Email": userEmail },
        credentials: "include",
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && Array.isArray(data.devices)) setDevices(data.devices as TrustedDevice[]);
    } finally {
      setDevicesLoading(false);
    }
  }, [userEmail]);

  useEffect(() => {
    void load();
    void loadDevices();
  }, [load, loadDevices]);

  const revokeDevice = async (id: string) => {
    setError(null);
    setBusy(true);
    try {
      const res = await fetch(`/api/users/me/trusted-devices/${encodeURIComponent(id)}`, {
        method: "DELETE",
        headers: { "X-User-Email": userEmail },
        credentials: "include",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || translateUi("Could not remove this device"));
      await loadDevices();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : translateUi("Could not remove this device"));
    } finally {
      setBusy(false);
    }
  };

  const revokeAllDevices = async () => {
    setError(null);
    setBusy(true);
    try {
      const res = await fetch("/api/users/me/trusted-devices", {
        method: "DELETE",
        headers: { "X-User-Email": userEmail },
        credentials: "include",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || translateUi("Could not sign out other devices"));
      await loadDevices();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : translateUi("Could not sign out other devices"));
    } finally {
      setBusy(false);
    }
  };

  const startReenroll = async () => {
    setError(null);
    setBusy(true);
    try {
      const res = await fetch("/api/users/mfa/re-enroll", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-User-Email": userEmail },
        body: JSON.stringify({ currentCode }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || translateUi("Could not start re-enrollment"));
      setQr(String(data.qrDataUrl || ""));
      setSecret(String(data.secret || ""));
      setConfirmCode("");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : translateUi("Could not start re-enrollment"));
    } finally {
      setBusy(false);
    }
  };

  const confirmReenroll = async () => {
    setError(null);
    setBusy(true);
    try {
      const res = await fetch("/api/users/mfa/setup/confirm", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-User-Email": userEmail },
        body: JSON.stringify({ code: confirmCode }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || translateUi("Incorrect authenticator code"));
      setBackupCodes(Array.isArray(data.backupCodes) ? data.backupCodes.map(String) : []);
      setQr("");
      setSecret("");
      setCurrentCode("");
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : translateUi("Incorrect authenticator code"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 sm:p-8 w-full">
      <h2 className="text-xl font-bold text-slate-900 mb-2 flex items-center gap-2">
        <ShieldCheck className="w-6 h-6 text-indigo-600" />
        {translateUi("Two-factor authentication")}
      </h2>
      <p className="text-slate-600 text-sm mb-6">
        {translateUi("An authenticator app adds a second step when signing in. You cannot turn this off yourself — Rail Intel manages the requirement.")}
      </p>
      {error && <div className="p-3 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm mb-4">{error}</div>}
      {loading ? (
        <p className="text-sm text-slate-500">{translateUi("Loading…")}</p>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm">
            {enabled ? (
              <>
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <span className="text-slate-800">{translateUi("Authenticator app is set up")}</span>
              </>
            ) : (
              <span className="text-slate-600">{translateUi("No authenticator enrolled yet. You will be asked to set one up at sign-in if it is required.")}</span>
            )}
          </div>
          {enabled && (
            <p className="text-sm text-slate-600">
              {translateUi("Backup codes remaining")}: <span className="font-semibold">{remaining}</span>
            </p>
          )}
          {enabled && !qr && !backupCodes && (
            <div className="space-y-3 pt-2">
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider">
                {translateUi("Current authenticator or backup code")}
              </label>
              <input
                type="text"
                inputMode="text"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                autoComplete="off"
                maxLength={9}
                placeholder={translateUi("123456 or XXXX-XXXX")}
                className="w-full max-w-xs px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl font-mono tracking-wider uppercase"
                value={currentCode}
                onChange={(e) => {
                  const compact = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8);
                  if (/[A-Z]/.test(compact) || compact.length > 6) {
                    setCurrentCode(compact.length > 4 ? `${compact.slice(0, 4)}-${compact.slice(4)}` : compact);
                    return;
                  }
                  setCurrentCode(compact.slice(0, 6));
                }}
              />
              <p className="text-xs text-slate-500">
                {translateUi("Use the 6-digit app code, or one of the backup codes you saved when you set up your authenticator.")}
              </p>
              <button
                type="button"
                disabled={
                  busy ||
                  !(
                    /^\d{6}$/.test(currentCode) ||
                    currentCode.toUpperCase().replace(/[^A-Z0-9]/g, "").length === 8
                  )
                }
                onClick={() => void startReenroll()}
                className="px-5 py-2.5 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 disabled:opacity-50 text-sm"
              >
                {translateUi("Re-enroll authenticator")}
              </button>
            </div>
          )}
          {qr && (
            <div className="space-y-3">
              <img src={qr} alt="" width={180} height={180} className="rounded-xl border border-slate-200" />
              <p className="font-mono text-sm tracking-widest text-slate-700">{secret}</p>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                className="w-full max-w-xs px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl"
                placeholder={translateUi("New 6-digit code")}
                value={confirmCode}
                onChange={(e) => setConfirmCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              />
              <button
                type="button"
                disabled={busy || confirmCode.length !== 6}
                onClick={() => void confirmReenroll()}
                className="px-5 py-2.5 bg-indigo-600 text-white font-bold rounded-xl text-sm disabled:opacity-50"
              >
                {translateUi("Confirm new authenticator")}
              </button>
            </div>
          )}
          {backupCodes && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-slate-800">{translateUi("Save these backup codes now. They will not be shown again.")}</p>
              <div className="grid grid-cols-2 gap-2 max-w-sm">
                {backupCodes.map((c) => (
                  <div key={c} className="font-mono text-sm bg-slate-50 rounded-xl px-3 py-2 text-center">
                    {c}
                  </div>
                ))}
              </div>
              <button type="button" className="text-sm text-indigo-600" onClick={() => setBackupCodes(null)}>
                {translateUi("Done")}
              </button>
            </div>
          )}
        </div>
      )}

      <div className="mt-8 pt-6 border-t border-slate-200">
        <h3 className="text-base font-bold text-slate-900 mb-1">{translateUi("Trusted devices")}</h3>
        <p className="text-sm text-slate-600 mb-4">
          {translateUi("Browsers you marked as trusted can skip 2FA for 14 days, unless you sign in from a new location.")}
        </p>
        {devicesLoading ? (
          <p className="text-sm text-slate-500">{translateUi("Loading…")}</p>
        ) : devices.length === 0 ? (
          <p className="text-sm text-slate-500">{translateUi("No trusted devices. Tick the box on the 2FA screen next time you sign in from a personal computer.")}</p>
        ) : (
          <ul className="space-y-3">
            {devices.map((d) => (
              <li key={d.id} className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800">
                    {d.deviceLabel}
                    {d.current ? (
                      <span className="ml-2 text-xs font-semibold text-emerald-700">{translateUi("This browser")}</span>
                    ) : null}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {d.country ? `${d.country} · ` : ""}
                    {translateUi("Last used")} {formatWhen(d.lastUsedAt)}
                  </p>
                </div>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void revokeDevice(d.id)}
                  className="shrink-0 p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg disabled:opacity-50"
                  aria-label={translateUi("Remove trusted device")}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
        {devices.length > 0 && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void revokeAllDevices()}
            className="mt-3 text-sm font-medium text-rose-700 hover:text-rose-800 disabled:opacity-50"
          >
            {translateUi("Sign out all trusted devices")}
          </button>
        )}
      </div>
    </div>
  );
};
